<template>
  <div class="term__wrapper">
    <div class="term__main">
      <a-term-details
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.TERM_DETAILS
          )
        "
        :title="term.Name"
        :description="term.Description"
        :hide-description="isInitialPagePaginated"
        :term-id="term.Id"
        :term-name="term.Name"
        class="term__details"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.TERM_DETAILS
          )
        "
      />

      <i class="term__horizontal-delimiter" />

      <a-lazy-hydrate when-visible>
        <a-action-icon-list
          :action-icons="actionIcons"
          class="term__action-icons"
          v-on="$listeners"
        />
      </a-lazy-hydrate>

      <a-visibility hide :on="[$breakpoint.mobile]">
        <div>
          <a-lazy-hydrate v-if="!isInitialPagePaginated" when-visible>
            <a-related-terms
              :related-terms="term.RelatedTerms"
              class="term__related-terms"
            />
          </a-lazy-hydrate>
        </div>
      </a-visibility>

      <a-visibility show :on="[$breakpoint.mobile]">
        <div>
          <a-lazy-hydrate when-visible>
            <a-related-terms
              :related-terms="term.RelatedTerms"
              class="term__related-terms"
            />
          </a-lazy-hydrate>
        </div>
      </a-visibility>

      <div v-if="isRelatedArticlesExist" class="term__related-articles-wrapper">
        <i class="term__horizontal-delimiter" />
        <p class="term__related-articles-title title">Related Articles</p>

        <a-article-list :articles="relatedArticles" class="term__article-list">
          <template v-slot="{ article }">
            <a-lazy-hydrate
              when-visible
              :key="
                $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                  article.articleId
                )
              "
            >
              <a-article-slot
                v-bind="getArticleSlotProps(article)"
                class="term__related-article"
                @mounted="
                  $_lazyHydratedComponentState_onHydratedComponentMounted(
                    article.articleId
                  )
                "
              />
            </a-lazy-hydrate>
          </template>
        </a-article-list>

        <a-view-more
          :page="pagination.page"
          :pages-count="pagesCount"
          :in-progress="inProgress"
          class="term__view-more"
          :view-more-style="$options.consts.VIEW_MORE_STYLE.BUTTON"
          @click="getNextArticleChunk"
        />
      </div>
    </div>

    <a-url-pagination
      :item-selector="ARTICLE_SLOT_SELECTOR"
      :item-count="relatedArticles.length"
      :initial-page="initialPage"
      :pagination.sync="pagination"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-refresh-listener
      :articles.sync="relatedArticles"
      :feed-refresh-in-progress.sync="feedRefreshInProgress"
      :slot-refresh-in-progress.sync="slotRefreshInProgress"
      :fetch-data-fn="fetchLoadedArticles"
      :target-term-slug="$route.params.term"
      :settings="$options.consts.AUTO_REFRESH_SETTINGS.TERM_PAGE"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import AArticleList from 'shared/AArticleList'
import AArticleSlot from 'shared/AArticleSlot/index.vue'
import AViewMore from 'shared/AViewMore/index.vue'
import { VIEW_MORE_STYLE } from 'shared/AViewMore/enums'
import ATermDetails from '@/components/_sections/terms/ATermDetails'
import ARelatedTerms from '@/components/_sections/terms/ARelatedTerms'
import { ERROR_CODE } from 'enums/errorCodes'
import { throwPageNotFoundError } from '@/plugins/error'
import { getPageNumberFromRoute, createPrefilledArray } from '@/plugins/helper'
import { getParsedHtmlWithReplacedImageProps } from '@/utils/helpers/replaceImageProperties'
import AUrlPagination from 'shared/AUrlPagination'
import { SPINNER_ALIGNMENT } from 'shared/AInProgress'
import ARefreshListener from 'shared/ARefreshListener'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { NEWS_ITEM_DATA_ATTRIBUTE } from 'enums/data-attributes'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { generateTermUrl } from '@/utils/helpers/url-generators'
import { AUTO_REFRESH_SETTINGS } from 'enums/article-auto-refresh'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import AActionIconList from 'shared/AActionIconList'
import { ACTION_ICON } from 'shared/AActionIcon'
import { applyFallback } from '@/utils/helpers'
import { pick } from 'ramda'

const HYDRATED_SECTION_NAME = {
  TERM_DETAILS: 'TERM_DETAILS'
}

const TERM_MODEL = {
  Id: '',
  Name: '',
  Description: '',
  DescriptionImages: [],
  DescriptionVideos: [],
  RelatedTerms: []
}

const DEFAULT_TAKE = 15
const ARTICLE_SLOT_SELECTOR = '.term__related-article-slot'

function checkTermRouteParams({ term, letter }) {
  return term.charAt(0).toLowerCase() === letter.toLowerCase()
}

function throwError(ctx) {
  const err = {
    response: {
      status: 404,
      data: { ErrorCode: ERROR_CODE.TERM_NOT_FOUND }
    }
  }
  ctx.app.$error(err)
}

function generateDefaultTermPageContentDataModel() {
  return {
    term: {},
    relatedArticles: [],
    pagesCount: 0
  }
}

async function getTermPageData({ store, route }) {
  const termSlug = route.params.term

  const dispatch = store.dispatch
  const initialPage = getPageNumberFromRoute(route)
  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }

  const [term, { Articles, PagesCount: pagesCount }] = await Promise.all([
    dispatch('terms/requestTermBySlug', { termSlug }),
    dispatch('terms/requestRelatedArticles', {
      termSlug,
      ...pagination
    })
  ])

  return {
    initialPage,
    pagination,
    isInitialPagePaginated: !!initialPage,
    term: applyFallback(
      {
        ...term,
        Description: getParsedHtmlWithReplacedImageProps(
          term.Description,
          term.DescriptionImages
        )
      },
      TERM_MODEL
    ),
    relatedArticles: Articles,
    pagesCount
  }
}

export default {
  name: 'TermSlug',
  scrollToTop: true,
  mixins: [
    mixins.articleDataHandler,
    mixins.delayedContentRefresh,
    mixins.lazyHydratedComponentState
  ],
  components: {
    AViewMore,
    ATermDetails,
    ARelatedTerms,
    AArticleList,
    AArticleSlot,
    AUrlPagination,
    ARefreshListener,
    APaginationLinksForGoogle,
    AActionIconList
  },
  consts: {
    NEWS_ITEM_DATA_ATTRIBUTE,
    AUTO_REFRESH_SETTINGS,
    VIEW_MORE_STYLE,
    HYDRATED_SECTION_NAME
  },
  head() {
    const { Name } = this.term

    return this.$generateMetaTags({
      titleParam: Name,
      descriptionParam: Name,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        ...this.$helper.generateVideoSchemasFromResponse(
          this.term.DescriptionVideos
        )
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      if (!checkTermRouteParams(ctx.route.params)) return throwError(ctx)

      const data = await getTermPageData({ store: ctx.store, route: ctx.route })

      const canonicalUrlWithoutPagination = generateTermUrl(
        { slug: data.term.Slug },
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...data,
        canonicalUrl,
        canonicalUrlWithoutPagination,
        feedRefreshInProgress: false,
        slotRefreshInProgress: createPrefilledArray(
          data.relatedArticles.length,
          false
        )
      }
    } catch (err) {
      throwPageNotFoundError(ctx, 'TermPage')
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultTermPageContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      ARTICLE_SLOT_SELECTOR,
      SPINNER_ALIGNMENT,
      initialPage,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      inProgress: false,
      feedRefreshInProgress: false,
      slotRefreshInProgress: [],
      isInitialPagePaginated: false,
      HTML_TAG
    }
  },
  computed: {
    isRelatedArticlesExist() {
      return !!this.relatedArticles.length
    },
    actionIcons() {
      const { fullPath: path } = this.$route

      return [
        {
          actionIcon: ACTION_ICON.TWITTER,
          title: this.term.Name,
          path,
          ariaLabel: 'Twitter'
        },
        {
          actionIcon: ACTION_ICON.TELEGRAM,
          title: this.term.Name,
          path,
          ariaLabel: 'Telegram'
        },
        {
          actionIcon: ACTION_ICON.FACEBOOK,
          title: this.term.Name,
          path,
          ariaLabel: 'Facebook'
        }
      ]
    }
  },
  methods: {
    ...mapActions({
      requestRelatedArticles: 'terms/requestRelatedArticles'
    }),
    getArticleSlotProps(article) {
      return {
        ...article,
        [this.$options.consts.NEWS_ITEM_DATA_ATTRIBUTE]: true
      }
    },
    async getRelatedArticles() {
      try {
        this.inProgress = true
        const payload = {
          termSlug: this.$route.params.term,
          ...this.pagination
        }
        const { Articles, PagesCount } = await this.requestRelatedArticles(
          payload
        )

        this.pagesCount = PagesCount
        this.relatedArticles = [...this.relatedArticles, ...Articles]
        this.slotRefreshInProgress = createPrefilledArray(
          this.relatedArticles.length,
          false
        )
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      this.getRelatedArticles()
    },
    async fetchLoadedArticles() {
      const articleChunks = await this.$_articleDataHandler_fetchLoadedArticles(
        {
          action: this.requestRelatedArticles,
          initialPage: this.initialPage,
          take: this.pagination.take,
          length: this.relatedArticles.length,
          payload: { termSlug: this.$route.params.term }
        }
      )
      return articleChunks.reduce((acc, { Items }) => [...acc, ...Items], [])
    },
    refreshTermPageContent() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultTermPageContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getTermPageData({
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified', 'ModifiedOn'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))

          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            Object.values(HYDRATED_SECTION_NAME)
          )

          const componentIdentifiers = this.relatedArticles.map(
            article => article.articleId
          )

          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            componentIdentifiers
          )
        }
      })
    }
  },
  mounted() {
    this.refreshTermPageContent()
  }
}
</script>

<style lang="scss" scoped>
.term__wrapper {
  .term__container {
    margin-top: 20px;
  }
}

/deep/ .article-body-normal {
  @include tablet {
    margin-bottom: 0;
  }
}

.term__horizontal-delimiter {
  display: block;
  width: 100%;
  height: 1px;
  background: $c--gray-light;
}

.term__action-icons {
  margin: 24px 0;
}

.term__related-terms {
  @include mobile {
    margin-top: 20px;
  }
}

.term__related-articles-title {
  margin: 40px 0;
  font-weight: 500;

  @include tablet {
    margin: 24px 0;
  }
}

.term__related-article {
  margin-bottom: 20px;
  padding-bottom: 20px;
}

/deep/ .article-list__item-wrapper:last-child .term__related-article {
  margin-bottom: 0;
}

.term__banner {
  margin-top: 24px;
  margin-bottom: 24px;
}

.term__view-more {
  margin-top: 20px;
}

.term__no-news-found {
  text-align: center;
}
</style>
